import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';

interface SearchAutocompleteProps {
  onPlaceSelected: (place: google.maps.places.PlaceResult | null) => void;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({ onPlaceSelected }) => {
  const container = useRef<HTMLDivElement>(null);
  const onSelect = useRef(onPlaceSelected);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onSelect.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    let disposed = false;
    let selection = 0;
    let autocomplete: google.maps.places.PlaceAutocompleteElement | undefined;

    const onPlaceChanged = async ({ placePrediction }: google.maps.places.PlacePredictionSelectEvent) => {
      const currentSelection = ++selection;
      setError(null);

      try {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ['location'] });
        if (disposed || currentSelection !== selection) return;
        if (!place.location) {
          setError('The selected place has no coordinates. Please choose another place.');
          return;
        }

        // Keep the coordinate shape consumed by property, cluster, and nearby forms.
        onSelect.current({ geometry: { location: place.location } });
      } catch {
        if (!disposed && currentSelection === selection) {
          setError('Unable to load the selected location. Please try again.');
        }
      }
    };

    const onRequestError = () => {
      ++selection;
      setError('Unable to search locations. Check that Places API (New) is enabled and the API key is authorized.');
    };

    const initialize = async () => {
      try {
        const { PlaceAutocompleteElement } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
        if (disposed) return;

        autocomplete = new PlaceAutocompleteElement({});
        autocomplete.setAttribute('placeholder', 'Search for a place');
        autocomplete.setAttribute('aria-label', 'Search for a place');
        autocomplete.style.width = '100%';
        autocomplete.addEventListener('gmp-select', onPlaceChanged);
        autocomplete.addEventListener('gmp-error', onRequestError);
        container.current?.appendChild(autocomplete);
      } catch {
        if (!disposed) onRequestError();
      }
    };

    initialize();

    return () => {
      disposed = true;
      autocomplete?.removeEventListener('gmp-select', onPlaceChanged);
      autocomplete?.removeEventListener('gmp-error', onRequestError);
      autocomplete?.remove();
    };
  }, []);

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <div ref={container} />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
};

export default SearchAutocomplete;
